import BaseMenu from 'System/UI/Menu/BaseMenu';
import RelativePositionComponent from 'Component/Render/RelativePosition';
import Point from 'Utility/Point';
import { applyDefaultButtonAnimation } from 'System/UI/UIHelper';
import { GameObjectEvent } from 'GameObject/GameObject';
import { UIActions } from 'State/UI/UIActions';
import { createEventOfferTimer, getEventOfferData } from 'State/Events/EventHelper';
import { EventType } from 'Utility/Helpers/Events/EventType';
import Game from 'Game';
import Timer from 'System/Timer/Timers/Timer';
import TextComponent from 'Component/Render/Text';
import LevelHelper from 'Utility/LevelHelper';
import { formatStrikePrice, parseCurrency } from 'Utility/Currency';
import { report } from 'report';
import { IAPHelper } from 'Utility/Helpers/IAP/IAPHelper';
import { OfferType } from 'Utility/Helpers/IAP/Offers/OfferTypes/OfferType';
import { ProductId } from 'Utility/Product';
import { unexpectedValue } from 'Utility/Types';
import { LanguageKey } from 'Utility/SDK/Language';
import { IAPCatalog } from 'Utility/SDK/Drivers/IAPCatalog';
import BaseUISystem from 'System/UI/BaseUISystem';
import { PopupID } from 'State/UI/PopupID';
import { EasterEventOfferLayout } from 'Layouts/Events/easter_2025/eventOfferPopup/EasterEventOfferLayout';
import { getLayout, GLayout } from 'Utility/layout/LayoutHelper';
import Renderable from 'Component/Renderable';
import TransformComponent from 'Component/Transform';
import { getHighestLevelCompleted } from 'State/Player/PlayerLevelHelper';
import { GameActions } from 'State/Game/GameActions';
import { ContextType } from 'State/Game/ContextType';
import { IAPReward } from 'Utility/RewardHelper';
import { IAPStateHelper } from 'State/Player/IAP/IAPStateHelper';
import { IGameState } from 'State/Game/IGameState';
import Interactable from 'Component/Interactable';
import { PlayerActions } from 'State/Player/PlayerActions';
import { IMultiOfferTier } from 'Utility/Helpers/IAP/Offers/OfferTypes/IMultiOfferTier';

interface IEventOfferShowParams {
    eventType: EventType;
}

export class EasterEventOfferHandler extends BaseMenu {
    private layout: GLayout<EasterEventOfferLayout>;
    private timer: Timer;

    private eventType: EventType = EventType.EASTER_2025;

    private throttleBuyButton: boolean = false;

    constructor(uiSystem: BaseUISystem) {
        super(PopupID.EVENT_OFFER, uiSystem, false);
    }

    public async preload() {
        this.resources.set('IAPRewards', `resource/data/rewards/IAPRewards.json`);
        await super.preload();
    }

    protected onCreate(): void {
        super.onCreate();
        this.layout = getLayout(EasterEventOfferLayout);

        this.layout.root.addComponent(RelativePositionComponent);
        this.layout.root.getComponent(RelativePositionComponent).relativePosition = new Point(0.5, 0.5);
        this.element.addChild(this.layout.root);
    }

    public onShow(params: IEventOfferShowParams): void {
        super.onShow(params);

        this.throttleBuyButton = false;

        const state = Game.store.getState();
        const offerData = getEventOfferData(EventType.EASTER_2025);

        if (!offerData) {
            report('Failed to find offer data for event.', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    eventType: this.eventType,
                },
                fingerprint: 'EventOfferHandler:OnShow:FailedToFindOfferData',
            });
            UIActions.hidePopup(PopupID.EVENT_OFFER);
            return;
        }

        let offerTier = IAPHelper.getRelevantOfferTier(state, offerData);
        // Save spender tier for scalable offer the first time it's shown to the player
        if (
            !state.playerState.currentEventOffer ||
            state.playerState.currentEventOffer?.type !== this.eventType
        ) {
            offerTier = IAPHelper.getRelevantOfferTier(state, offerData);
            PlayerActions.setCurrentEventOffer({
                type: params.eventType,
                tier: offerTier
            });
        }
        else offerTier = state.playerState.currentEventOffer.tier;

        if (!offerTier) {
            report('Failed to find relevant offer for event.', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    eventType: this.eventType,
                },
                fingerprint: 'EventOfferHandler:OnShow:FailedToFindRelevantOfferTier',
            });
            UIActions.hidePopup(PopupID.EVENT_OFFER);
            return;
        }

        const { closeButton, timeText } = this.layout;

        applyDefaultButtonAnimation(closeButton);

        this.addListener(closeButton, GameObjectEvent.SELECTED, () => UIActions.hidePopup(this.name), { once: true });

        const timerTextComponent = timeText.getComponent(TextComponent);
        this.timer = createEventOfferTimer(state, this.eventType, timerTextComponent, {
            onComplete: () => {
                UIActions.hidePopup(this.name);
            },
        });

        this.timer.format = 'DD:hh:mm:ss';

        this.timer.start();

        this.cleanupManager.add(() => {
            this.timer.stop();
        });

        const catalog = Game.sdk.getCatalog();
        const language = Game.sdk.getLanguage();
        const levelCost = LevelHelper.getLevelCost(getHighestLevelCompleted(state.playerState));
        const rewardsMap = IAPHelper.CACHED_IAP_REWARDS_MAP();

        switch (offerTier.type) {
            case OfferType.SINGLE:
                this.setupProduct({ catalog, language, productId: offerTier.productId, levelCost, state, rewardsMap });
                break;
            case OfferType.MULTI:
                offerTier.products.forEach((product, index) => {
                    this.setupProduct(
                        {
                            catalog,
                            rewardsMap,
                            language,
                            productId: product.productId,
                            levelCost,
                            state,
                            previousProductId: index > 0 ? (offerTier as IMultiOfferTier).products[index - 1].productId : undefined,
                        },
                        index
                    );
                });
                break;
            default:
                unexpectedValue(offerTier, false);
        }
    }

    private setupProduct(
        params: {
            catalog: IAPCatalog;
            language: LanguageKey;
            productId: ProductId;
            levelCost: number;
            state: IGameState;
            previousProductId?: ProductId;
            rewardsMap: { [k: string]: IAPReward };
        },
        index: number = 0
    ) {
        const { catalog, language, productId, levelCost, state, previousProductId, rewardsMap } = params;
        const product = catalog.products.find(e => e.productId === productId);
        if (!product) {
            report('Failed to find event offer product', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    productId,
                },
                fingerprint: 'EventOfferHandler:onShow:FailedToFindProduct',
            });
            UIActions.hidePopup(PopupID.EVENT_OFFER);
            return;
        }

        const iapReward = rewardsMap[productId];

        if (!iapReward) {
            report('Failed to find event offer product reward data', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    productId,
                },
                fingerprint: 'EventOfferHandler:onShow:FailedToFindProductRewardData',
            });
            UIActions.hidePopup(PopupID.EVENT_OFFER);
            return;
        }

        // Setup button prices
        try {
            const { beforeValue, value, afterValue } = parseCurrency(product.price);

            const fullCostStr = value.toLocaleString(language, { maximumFractionDigits: 2, minimumFractionDigits: 2 });

            const strikePriceUsd = iapReward.usdPriceWithoutDiscount;
            const strikePrice = formatStrikePrice({
                price: product.price,
                priceUsd: iapReward.usdPrice,
                strikePriceUsd,
            });

            this.layout.products[index].buyButtons.forEach(button => {
                button.priceText.getComponent(TextComponent).text = `${beforeValue}${fullCostStr}${afterValue}`;
                button.oldPrice.getComponent(TextComponent).text = strikePrice;
            });
        } catch {
            report('Failed to parse currency for product', {
                once: true,
                sampleRate: 0.1,
                tags: {
                    language,
                    productId: product.productId,
                },
                fingerprint: 'EasterOfferMenu:OnShow:FailedToParseCurrency',
            });
            this.layout.products[index].buyButtons.forEach(button => {
                button.priceText.getComponent(TextComponent).text = product.price;
                button.priceText.getComponent(TransformComponent).position.y = 0;
                button.oldPrice.getComponent(Renderable).update({ visible: false });
                button.strike.getComponent(Renderable).update({ visible: false });
            });
        }

        // Add listener to button
        this.layout.products[index].buy_button.addComponent(Interactable);
        applyDefaultButtonAnimation(this.layout.products[index].buy_button);
        this.addListener(this.layout.products[index].buy_button, GameObjectEvent.SELECTED, () => {
            this.onBuyClick({ ...iapReward, rewards: iapReward.rewards });
        });

        this.layout.products[index].percent.getComponent(TextComponent).text = `${iapReward?.percent ?? 50}%`;

        // Update reward counts
        iapReward.rewards.forEach((reward, rewardIndex) => {
            this.layout.products[index].rewards[rewardIndex]?.value
                ?.getComponent(TextComponent)
                ?.update({ text: `x ${reward.value}` });
        });

        const hasPurchasedProduct = IAPStateHelper.hasPurchasedProduct(state, productId);
        const hasPurchasedPreviousProduct =
            !previousProductId || IAPStateHelper.hasPurchasedProduct(state, previousProductId);

        this.updateProductState(index, hasPurchasedProduct, hasPurchasedPreviousProduct);
    }

    private updateProductState(index: number, hasPurchasedProduct: boolean, hasPurchasedPreviousProduct: boolean) {
        this.layout.products[index].locked.getComponent(Renderable).visible =
            !hasPurchasedProduct && !hasPurchasedPreviousProduct;
        this.layout.products[index].unlocked.getComponent(Renderable).visible =
            !hasPurchasedProduct && hasPurchasedPreviousProduct;
        this.layout.products[index].purchased.getComponent(Renderable).visible = hasPurchasedProduct;
    }

    private onBuyClick(product: IAPReward): void {
        if (this.throttleBuyButton) {
            return;
        }

        this.throttleBuyButton = true;

        const timeout = Game.ticker.setTimeout(() => {
            if (this) this.throttleBuyButton = false;
        }, 700);

        this.cleanupManager.add(() => {
            if (timeout) Game.ticker.clearTimeout(timeout);
        });

        GameActions.setContext(ContextType.PURCHASE_PRODUCT, {
            product,
        });
    }

    private addListener(
        obj: GameObject,
        event: string,
        action: () => any,
        options?: {
            once?: boolean;
        }
    ) {
        if (options?.once) {
            obj.once(event, action);
        } else {
            obj.on(event, action);
        }
        this.cleanupManager.add(() => {
            obj.off(event, action);
        });
    }
}
